/** Shared presentation contracts for the installed application and browser. */
import type { NyxConnection } from './nyx-session.js';
import type { FilesConnection } from '@atum-remote/protocol/files';
import type { PrivilegeConnection } from '@atum-remote/protocol/linux-delegation';
import type { ServerInspectionConnection } from '@atum-remote/protocol/server-inspection';
import type { DeviceActionRequest, DeviceActionResult } from '@atum-remote/protocol/device-actions';
/** Tools that open in their own page or window: the desktop viewer, a terminal, and AI work. */
export type ConnectionTool = 'desktop' | 'terminal' | 'nyx';
/** Every tool a device page can show. Availability is specific to its host and connection. */
export type DeviceTool = ConnectionTool | 'files' | 'logs' | 'resources';
export type DevicePresence = 'online' | 'offline' | 'unchecked' | 'revoked';

export interface DeviceAction {
  /** Requires an implemented transport and authority, not just an advertised capability. */
  readonly available: boolean;
  readonly explanation: string;
  /** Explicit presentation hint. Every session still checks current server authority. */
  readonly controlAvailable?: boolean;
}

export interface RemoteDevice {
  /** Current Linux owner enrollment uses the headless infrastructure profile. */
  readonly hostProfile?: 'linux-server';
  readonly personalAccess?: boolean;
  readonly id: string;
  readonly name: string;
  readonly hostname: string;
  readonly platform: 'windows' | 'macos' | 'linux' | 'unknown';
  /** Original OS metadata for presentation, independent of supported connection platforms. */
  readonly reportedPlatform?: string;
  readonly osVersion: string;
  /** CPU architecture and client version as the device last reported them. */
  readonly architecture?: string;
  readonly appVersion?: string;
  /** Addresses the device reported when it last connected. Presentation only. */
  readonly network?: DeviceNetwork;
  readonly presence: DevicePresence;
  readonly group: string;
  readonly source: 'saved' | 'managed';
  readonly lastSeenAt: string | null;
  readonly desktop: DeviceAction;
  readonly terminal: DeviceAction;
  readonly files: DeviceAction;
  readonly logs: DeviceAction;
  readonly resources: DeviceAction;
  readonly nyx?: DeviceAction;
  readonly privileged?: DeviceAction;
}

export interface DeviceNetwork {
  readonly primaryIpv4?: string;
  readonly primaryIpv6?: string;
  readonly addresses: readonly {
    readonly interface: string;
    readonly address: string;
    readonly prefix: number;
  }[];
}

export interface ConnectionActivity {
  readonly id: string;
  readonly deviceName: string;
  readonly description: string;
  readonly at: string;
  readonly state: 'active' | 'ended' | 'failed' | 'event';
}

/** Public connection metadata only. SSH credentials stay outside application storage. */
export interface SavedConnectionInput {
  readonly name: string;
  readonly group: string;
  readonly sshAlias: string;
  readonly remoteProgram: string;
}

export interface DeviceSnapshot {
  readonly devices: readonly RemoteDevice[];
  readonly activity: readonly ConnectionActivity[];
}

/** Hosts implement authentication/transport. Shared views do not own either authority. */
export interface NativeUpdateOffer {
  /** Opaque native handle; UI cannot choose an update URL or bypass native signature verification. */
  readonly id: string;
  readonly version: string;
  readonly notes: string;
}
export interface NativeUpdateCheck {
  readonly currentVersion: string;
  readonly unavailableReason?:
    | 'managed_app_required'
    | 'signing_not_configured'
    | 'platform_unsupported'
    | null;
  readonly update: NativeUpdateOffer | null;
}
export interface NativeUpdater {
  check(): Promise<NativeUpdateCheck>;
  /** Native code verifies the signed payload, replaces the full master app, and restarts it. */
  install(
    id: string,
    onProgress: (progress: {
      downloaded: number;
      total?: number;
      phase: 'downloading' | 'installing';
    }) => void,
  ): Promise<void>;
}

export interface RemoteUiAdapter {
  readonly openInspection?: (device: RemoteDevice) => Promise<ServerInspectionConnection>;
  readonly runDeviceAction?: (
    device: RemoteDevice,
    request: DeviceActionRequest,
  ) => Promise<DeviceActionResult>;
  readonly openPrivileges?: (device: RemoteDevice) => Promise<PrivilegeConnection>;
  readonly openFiles?: (device: RemoteDevice) => Promise<FilesConnection>;
  readonly openNyx?: (device: RemoteDevice) => Promise<NyxConnection>;
  /** Native clients open validated download URLs in the system browser. */
  readonly openExternal?: (url: string) => Promise<void>;
  readonly updater?: NativeUpdater;
  readonly kind: 'desktop' | 'web';
  readonly supportsSavedConnections: boolean;
  readonly supportsSignIn: boolean;
  readonly signInConfigured: boolean;
  readonly accountName: string | undefined;
  /** The server's account class. Every action still uses the current device grant. */
  readonly accountAccess?: 'owner' | 'personal' | 'staff' | undefined;
  load(signal?: AbortSignal): Promise<DeviceSnapshot>;
  signIn(): void;
  signOut(): Promise<void>;
  saveConnection(input: SavedConnectionInput): Promise<void>;
  /** Saved SSH entries are forgotten; registered devices are retired for the whole account. */
  removeConnection(id: string): Promise<void>;
  openConnection(device: RemoteDevice, tool: ConnectionTool): Promise<void>;
}

/** Fallbacks for connections that do not implement these transports. */
export const previewTools: Readonly<Record<'files' | 'logs' | 'resources', DeviceAction>> = {
  files: { available: false, explanation: 'File transfer is not available in this version.' },
  logs: { available: false, explanation: 'Device logs are not available in this version.' },
  resources: {
    available: false,
    explanation: 'Resource monitoring is not available in this version.',
  },
};
